'use strict'
import React, { useState, useRef, useEffect, useCallback } from "react";
import Browse from "./Browse";
import loadSoundboardData from "./soundboardData";
import ReactPlayer from "react-player";
import { graphqlOperation } from "aws-amplify/api";
import { generateClient } from "aws-amplify/api";
import { getUrl, list, remove } from "aws-amplify/storage";

const client = generateClient();

// Configure Storage in aws-exports.js or manually in App2.js

function Client2() {
  console.log("Client2 component rendering");

  // State
  const [noMovies, setNoMovies] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [fileUrl, setFileUrl] = useState(null);
  const [loadedSounds, setLoadedSounds] = useState(0);
  const [totalSounds, setTotalSounds] = useState(0);
  const [isLoadingSounds, setIsLoadingSounds] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFsButton, setShowFsButton] = useState(true);

  // Refs
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const fsButtonTimerRef = useRef(null);
  const preloadedAudioRef = useRef({});
  const subscriptionRef = useRef(null);
  const hasInitialized = useRef(false);
  const soundLabelsRef = useRef({});
  const toastIdRef = useRef(0);

  // Preload a single sound with retry logic
  const preloadSound = useCallback(async (soundName, retryCount = 0) => {
    return new Promise((resolve) => {
      const url = `https://rm-brown-soundboard.com/sounds/drops/${soundName}.mp3`;
      const audio = new Audio();

      const onCanPlayThrough = () => {
        audio.removeEventListener('canplaythrough', onCanPlayThrough);
        audio.removeEventListener('error', onError);
        preloadedAudioRef.current[soundName] = audio;
        resolve(true);
      };

      const onError = async () => {
        audio.removeEventListener('canplaythrough', onCanPlayThrough);
        audio.removeEventListener('error', onError);

        if (retryCount < 1) {
          console.log(`Retrying sound: ${soundName}`);
          const success = await preloadSound(soundName, retryCount + 1);
          resolve(success);
        } else {
          console.log(`Failed to load sound after retry: ${soundName}`);
          resolve(false);
        }
      };

      audio.addEventListener('canplaythrough', onCanPlayThrough);
      audio.addEventListener('error', onError);
      audio.preload = 'auto';
      audio.src = url;
      audio.load();
    });
  }, []);

  // Load sounds in batches of 5
  const loadSoundsInBatches = useCallback(async (sounds) => {
    const BATCH_SIZE = 5;
    let loaded = 0;

    for (let i = 0; i < sounds.length; i += BATCH_SIZE) {
      const batch = sounds.slice(i, i + BATCH_SIZE);
      await Promise.all(
        batch.map(async (item) => {
          await preloadSound(item.name);
          loaded++;
          setLoadedSounds(loaded);
        })
      );
    }

    setIsLoadingSounds(false);
    console.log(`Finished preloading ${loaded} sounds`);
  }, [preloadSound]);

  // Handler for subscription messages
  const handleMessage = useCallback(async (input) => {
    // Extract message data
    console.log({ input });
    const msg = input.data.onCreateWatchwithToonsMessages;

    switch (msg.command) {
      case "play":
        try {
          const result = await getUrl({
            key: msg.name,
            options: {
              expiresIn: 10800,
              bucket: {
                bucketName: "watchwithtoonss3",
                region: "af-south-1",
              },
            },
          });
          setFileUrl(result.url.toString());
          setNoMovies(false);
          setPlaying(true);
        } catch (error) {}
        break;

      case "load":
        try {
          const result = await getUrl({
            key: msg.name,
            options: {
              expiresIn: 10800,
              bucket: {
                bucketName: "watchwithtoonss3",
                region: "af-south-1",
              },
            },
          });
          setFileUrl(result.url.toString());
          setNoMovies(false);
          setPlaying(false);
        } catch (error) {}
        break;

      case "stop":
        setFileUrl(null);
        setNoMovies(true);
        break;

      case "start":
        setPlaying(true);
        break;

      case "seek":
        console.log("got seek");
        if (playerRef.current) {
          playerRef.current.seekTo(parseFloat(msg.name), "seconds");
        }
        break;

      case "pause":
        setPlaying(false);
        break;

      case "checkbuffer":
        if (playerRef.current) {
        }
        break;

      case "unpause":
        setPlaying(true);
        break;

      case "catchup":
        try {
          const info = msg.name;
          const movieName = info.split("|")[0];
          const result = await getUrl({
            key: movieName,
            options: {
              expiresIn: 10800,
              bucket: {
                bucketName: "watchwithtoonss3",
                region: "af-south-1",
              },
            },
          });
          const timePosition = parseInt(info.split("|")[1]);

          setFileUrl(result.url.toString());
          setNoMovies(false);
          setPlaying(true);

          if (playerRef.current) {
            playerRef.current.seekTo(timePosition, "seconds");
          }
        } catch (error) {}
        break;

      case "soundboard":
        console.log("SB - received soundboard command:", msg.name);

        // Extract sound name from URL
        const urlMatch = msg.name.match(/\/([^/]+)\.mp3$/);
        if (urlMatch) {
          const soundName = urlMatch[1];

          // Show animated toast with the same label shown on the admin button
          const label = soundLabelsRef.current[soundName] || soundName.replace(/-/g, ' ');
          const id = ++toastIdRef.current;
          const angle = Math.random() * 2 * Math.PI;
          const dx = Math.cos(angle) * 120; // vw, guarantees off-screen horizontally
          const dy = Math.sin(angle) * 120; // vh, guarantees off-screen vertically
          setToasts((prev) => [...prev, { id, text: label, dx, dy }]);
          setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
          }, 3000);

          const preloadedAudio = preloadedAudioRef.current[soundName];

          if (preloadedAudio) {
            console.log("SB - playing preloaded sound:", soundName);
            preloadedAudio.currentTime = 0;
            preloadedAudio.volume = 1.0;
            preloadedAudio.play().catch((error) => {
              console.log("SB - play failed:", soundName, error);
            });
          } else {
            console.log("SB - sound not preloaded, loading on-demand:", soundName);
            const fallbackAudio = new Audio(msg.name);
            fallbackAudio.volume = 1.0;
            fallbackAudio.play().catch((error) => {
              console.log("SB - fallback play failed:", soundName, error);
            });
          }
        }
        break;

      default:
        break;
    }
  }, []);

  // Setup subscription
  useEffect(() => {
    // Prevent duplicate subscriptions using a persistent flag
    if (hasInitialized.current) {
      console.log("Subscription already initialized, skipping");
      return;
    }

    hasInitialized.current = true;
    console.log("Creating new subscription");

    async function setupSubscription() {
      try {
        // Import dynamically to avoid build issues
        const subscriptions = await import("./graphql/subscriptions");

        const subscription = client
          .graphql({
            query: subscriptions.onCreateWatchwithToonsMessages,
          })
          .subscribe({
            next: (data) => handleMessage(data),
            error: () => {},
          });

        subscriptionRef.current = subscription;
        console.log("Subscription created successfully");
      } catch (error) {
        console.error("Failed to create subscription:", error);
        hasInitialized.current = false; // Reset on error so it can retry
      }
    }

    setupSubscription();

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        console.log("Subscription cleaned up");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Track fullscreen state
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Auto-hide fullscreen button after 5s of no mouse movement
  useEffect(() => {
    const resetTimer = () => {
      setShowFsButton(true);
      clearTimeout(fsButtonTimerRef.current);
      fsButtonTimerRef.current = setTimeout(() => setShowFsButton(false), 5000);
    };
    resetTimer();
    window.addEventListener("mousemove", resetTimer);
    return () => {
      window.removeEventListener("mousemove", resetTimer);
      clearTimeout(fsButtonTimerRef.current);
    };
  }, []);

  // Fetch soundboard config and preload MP3s
  useEffect(() => {
    const loadSoundboard = async () => {
      try {
        const data = await loadSoundboardData();
        setTotalSounds(data.length);

        const labelMap = {};
        data.forEach((item) => {
          labelMap[item.name] = item.label || item.name.replace(/-/g, ' ');
        });
        soundLabelsRef.current = labelMap;

        console.log(`Starting to preload ${data.length} sounds in batches of 5`);

        await loadSoundsInBatches(data);
      } catch (error) {
        console.log('Failed to load soundboard:', error);
        setIsLoadingSounds(false);
      }
    };

    loadSoundboard();
  }, [loadSoundsInBatches]);

  // Toast overlay for soundboard button presses
  const toastOverlay = (
    <>
      <style>{`
        @keyframes rmToastFly {
          0%   { transform: translate(-50%, -50%) translate(0, 0); opacity: 1; }
          15%  { opacity: 1; }
          100% { transform: translate(-50%, -50%) translate(var(--dx), var(--dy)); opacity: 0; }
        }
        @keyframes rmToastColor {
          0%   { color: hsl(0, 100%, 60%); }
          8%   { color: hsl(30, 100%, 60%); }
          16%  { color: hsl(60, 100%, 60%); }
          25%  { color: hsl(90, 100%, 60%); }
          33%  { color: hsl(120, 100%, 60%); }
          41%  { color: hsl(150, 100%, 60%); }
          50%  { color: hsl(180, 100%, 60%); }
          58%  { color: hsl(210, 100%, 65%); }
          66%  { color: hsl(240, 100%, 68%); }
          75%  { color: hsl(270, 100%, 68%); }
          83%  { color: hsl(300, 100%, 65%); }
          91%  { color: hsl(330, 100%, 62%); }
          100% { color: hsl(360, 100%, 60%); }
        }
        .rm-toast {
          position: fixed;
          top: 50%;
          left: 50%;
          font-size: 2.5rem;
          font-weight: bold;
          white-space: nowrap;
          text-shadow: 2px 2px 6px rgba(0,0,0,0.85);
          animation: rmToastFly 3s ease-in forwards, rmToastColor 3s linear forwards;
        }
      `}</style>
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 99999,
          overflow: "hidden",
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="rm-toast"
            style={{ "--dx": `${t.dx}vw`, "--dy": `${t.dy}vh` }}
          >
            {t.text}
          </div>
        ))}
      </div>
    </>
  );

  // Render
  return (
    <>
    {toastOverlay}
    <div style={{ display: noMovies ? undefined : "none" }}>
      <Browse />
    </div>
    <div ref={containerRef} style={{ display: noMovies ? "none" : undefined, backgroundColor: "#000", color: "#666", position: "relative" }}>
      <button
        onClick={() => {
          if (isFullscreen) {
            document.exitFullscreen().catch(() => {});
          } else {
            containerRef.current?.requestFullscreen().catch(() => {});
          }
        }}
        style={{
          position: "fixed",
          top: 10,
          right: 10,
          zIndex: 100000,
          background: "rgba(0,0,0,0.5)",
          color: "#fff",
          border: "1px solid #888",
          borderRadius: 4,
          padding: "6px 10px",
          cursor: "pointer",
          opacity: showFsButton ? 1 : 0,
          transition: "opacity 0.3s",
          pointerEvents: showFsButton ? "auto" : "none",
        }}
      >
        {isFullscreen ? "Regular screen" : "Fullscreen"}
      </button>
      <ReactPlayer
        playing={playing}
        url={fileUrl}
        ref={playerRef}
        controls
        width="100%"
        height="100vh"
        volume={1}
        muted={false}
        config={{ file: { attributes: { controlsList: "nofullscreen" } } }}
      />
    </div>
    </>
  );
}

export default Client2;
